import React, { useEffect, useRef } from 'react';
import { styled } from 'styled-components';
import { Select, Option } from '@ingka/select';
import { useTranslation } from 'react-i18next';
import { useStateDispatch, useStateSelector } from '../store/';
import {
  clearSelectedTimeSlot,
  updateSelectedLanguage,
} from '../reducers/app/appReducer';
import analyticsApiClient from '../services/analytics';
import { type Translations } from '../services/languages/model';
import {
  clearAvailableTimeSlots,
  loadTimeSlotsData,
} from '../reducers/timeslots/timeSlotsReducer';
import { sendEventToOptimizely } from '../optimizely';

interface LanguagePickerPropTypes {
  languages: Translations[];
  serviceId: string;
  operation: string;
  isDisabled?: boolean;
}

const StyledSelect = styled(Select)`
  min-width: 200px;
`;

const LanguagePicker: React.FC<LanguagePickerPropTypes> = ({
  languages,
  serviceId,
  operation,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const dispatch = useStateDispatch();
  const { sendEventToEpisod } = analyticsApiClient();
  const selectedLanguage = useStateSelector(
    (state) => state.app.selected.language,
  );

  // Ensure the default language exists in the available languages
  const defaultLanguage =
    languages.find(
      (lang) => lang.code === (selectedLanguage || CSS_CONFIG.language),
    )?.code ||
    languages[0]?.code ||
    CSS_CONFIG.language;

  const selectRef = useRef<HTMLSelectElement | null>(null);
  const pendingFocusRestore = useRef(false);

  useEffect(() => {
    if (!isDisabled && pendingFocusRestore.current) {
      selectRef.current?.focus();
      pendingFocusRestore.current = false;
    }
  }, [isDisabled]);

  const onLanguageSelect = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    selectRef.current = event.currentTarget;
    pendingFocusRestore.current = true;
    const { value } = event.currentTarget;
    dispatch(updateSelectedLanguage(value));
    dispatch(clearAvailableTimeSlots());
    dispatch(clearSelectedTimeSlot());
    dispatch(loadTimeSlotsData({ language: value }));
    const eventName = `switching_language_${serviceId}_${operation}_appointment`;
    sendEventToEpisod(eventName);
    sendEventToOptimizely(eventName);
  };

  return (
    <StyledSelect
      onChange={onLanguageSelect}
      id="language"
      label={t('components.languagePicker.selectLabel')}
      hintText=""
      value={defaultLanguage}
      disabled={isDisabled}
      data-cy="language-picker"
    >
      {languages.map((language) => (
        <Option
          name={language.name}
          value={language.code}
          key={language.code}
        />
      ))}
    </StyledSelect>
  );
};

export default LanguagePicker;
